import { lazy } from 'react';
import * as Sentry from '@sentry/react';

export const RELOAD_ABORTED_MS = 10_000;

const STORAGE_KEY = 'cms.chunk-reload-build';

let reloadInFlight = false;

// Deploys build with `npm run release`, which stamps the commit here. Keying the
// guard on the build, rather than on a clock, is what bounds recovery to one reload
// per deploy; a build without the stamp degrades to one reload per tab session.
const currentBuild = () => import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

const readReloadedBuild = () => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const markReloadedBuild = (build) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, build);
    // Storage can accept the write and still not keep it (private modes, embedded
    // webviews). A lost mark would make every boot reload again, forever.
    return window.sessionStorage.getItem(STORAGE_KEY) === build;
  } catch {
    return false;
  }
};

export function requestChunkReload() {
  if (reloadInFlight) return true;

  const build = currentBuild();

  if (readReloadedBuild() === build) {
    Sentry.addBreadcrumb({
      category: 'chunk-reload',
      message: 'A dynamic import failed again after this build was reloaded',
      level: 'warning',
    });

    return false;
  }

  if (!markReloadedBuild(build)) return false;

  reloadInFlight = true;
  window.location.reload();

  return true;
}

export function registerPreloadErrorListener() {
  // Never preventDefault: Vite would then resolve the import with undefined
  // instead of rejecting, and React.lazy would crash reading `.default`.
  window.addEventListener('vite:preloadError', () => {
    requestChunkReload();
  });
}

export async function importWithReload(factory) {
  try {
    return await factory();
  } catch (error) {
    return new Promise((resolve, reject) => {
      if (!requestChunkReload()) {
        reject(error);
        return;
      }

      // An unsaved-changes guard can cancel the reload. Without this the route
      // would stay suspended forever on a page that never navigates.
      setTimeout(() => {
        reloadInFlight = false;
        reject(error);
      }, RELOAD_ABORTED_MS);
    });
  }
}

export default function lazyWithReload(factory) {
  return lazy(() => importWithReload(factory));
}
